import { FullLoading } from "components/FullLoading";
import { IntegrationComponent } from "components/IntegrationComponent";
import { MainContainer } from "components/MainContainer";
import { Toast } from "components/toast";

import type { UpdateVacationPolicyFormValuesT } from "./domain/types/vacationPolicy.types";

import VacationPolicyForm from "./components/VacationPolicyForm";

import { VACATION_STRINGS } from "shared/constants/vacationStrings";
import { VACATION_POLICY_STRINGS } from "./constants/strings";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useQueryError } from "hooks/useQueryError";
import useReload from "hooks/useReload";

import { useVacation } from "./hooks";

const Policy = () => {
  const currentCompany = useCurrentCompany();

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    updateVacationPolicy: { isPending: isUpdating, mutateAsync: updatePolicy },
  } = useVacation({ colaboraInstanceId: currentCompany?.instanceId ?? "" });

  useReload([refetch]);
  useQueryError({
    isError,
    error,
    message: VACATION_POLICY_STRINGS.GET_DATA_ERROR_TITLE,
  });

  const handleOnSubmit = async (
    formValues: UpdateVacationPolicyFormValuesT,
  ) => {
    if (isError) {
      Toast(
        "warning",
        VACATION_POLICY_STRINGS.UPDATE_DATA_WARNING_TITLE,
        VACATION_POLICY_STRINGS.UPDATE_DATA_WARNING_MESSAGE,
      );

      return;
    }

    try {
      await updatePolicy({
        type: {
          id: data?.type?.id ?? "",
          icon: formValues.icon,
          code: data?.type?.code ?? "",
          name: data?.type?.name ?? "",
          description: data?.type?.description,
          isJustified: data?.type?.isJustified ?? true,
        },
        policy: {
          minimumEligibilityDays: formValues.minimumEligibilityDays,
          accrualMode: formValues.accrualMode,
          allowNegativeBalance: formValues.allowNegativeBalance,
          minAdvanceNoticeDays: formValues.minAdvanceNoticeDays,
          maxBackdateDays: formValues.maxBackdateDays,
          maxAdvanceDays: data?.policy?.maxAdvanceDays,
          maxNegativeBalanceDays: data?.policy?.maxNegativeBalanceDays,
          approvalFlow: data?.policy?.approvalFlow,
          requiresAttachment: data?.policy?.requiresAttachment,
        },
        policyId: data?.policy?.id,
      });

      Toast("success", VACATION_POLICY_STRINGS.UPDATE_DATA_SUCCESS_TITLE);
    } catch (err) {
      Toast(
        "error",
        VACATION_POLICY_STRINGS.UPDATE_DATA_ERROR_TITLE,
        (err as Error).message,
      );
    }
  };

  return (
    <IntegrationComponent
      message={VACATION_STRINGS.INTEGRATION_MESSAGE}
      confirmButtonLabel={VACATION_STRINGS.INTEGRATION_CONFIRM_BUTTON_LABEL}
    >
      <MainContainer
        cancelTittle
        nameScreen={VACATION_POLICY_STRINGS.TITLE}
        dataTestId="policy-main-container"
        alertComponent={null}
      >
        {isLoading && <FullLoading loadingState={isLoading} />}
        <div className="flex w-full h-full justify-center items-center content-center relative p-2 bg-gray-50">
          <VacationPolicyForm
            data={data}
            hasFetchingDataError={isError}
            isUpdating={isUpdating}
            onSubmitForm={handleOnSubmit}
            dataTestId="vacation-policy-form"
          />
        </div>
      </MainContainer>
    </IntegrationComponent>
  );
};

export default Policy;
